import { extractProductPathFromUrl } from '~/components/ConsumerSideNavigation/helpers'
import {
  ApiContent,
  ApiContentDiff,
  ApiTocItem,
  CONTENT_TYPE,
  DocumentContent,
  DocumentContentDiff,
  DocumentTocItem,
  GraphQlContent,
  GraphQlContentDiff,
  GraphQlTocItem,
  PublishedTableOfContents,
  TableOfContentsItem,
  TableOfContentsItemDiff
} from '~/types/content'
import { TemplateApiContent, TemplateContent, TemplateDocumentContent } from '~/types/template'

export const isDocumentContentGeneric = <T>(item: T): boolean =>
  (item as any)?.content?.type === CONTENT_TYPE.HTML || (item as any)?.content?.type === CONTENT_TYPE.MARKDOWN

export const isDocumentContent = (item: TableOfContentsItem): item is DocumentContent =>
  item.content?.type === CONTENT_TYPE.HTML || item.content?.type === CONTENT_TYPE.MARKDOWN

export const isTemplateDocumentContent = (item: TemplateContent): item is TemplateDocumentContent =>
  item.content?.type === CONTENT_TYPE.HTML || item.content?.type === CONTENT_TYPE.MARKDOWN

export const isDocumentContentDiff = (item: TableOfContentsItemDiff): item is DocumentContentDiff =>
  item.content?.type === CONTENT_TYPE.HTML || item.content?.type === CONTENT_TYPE.MARKDOWN

export const isApiContentDiff = (item: TableOfContentsItemDiff): item is ApiContentDiff =>
  item.content?.type === CONTENT_TYPE.API_URL

export const isGraphQlContentDiff = (item: TableOfContentsItemDiff): item is GraphQlContentDiff =>
  item.content?.type === CONTENT_TYPE.GRAPHQL

export const isApiContent = (item: TableOfContentsItem): item is ApiContent =>
  item.content?.type === CONTENT_TYPE.API_URL

export const isGraphQlContent = (item?: TableOfContentsItem): item is GraphQlContent =>
  item?.content?.type === CONTENT_TYPE.GRAPHQL

export const isTemplateApiContent = (item: TemplateContent): item is TemplateApiContent =>
  item.content?.type === CONTENT_TYPE.API_URL

export const isPublishedDocumentContent = (content?: PublishedTableOfContents): content is DocumentTocItem =>
  content?.type === CONTENT_TYPE.HTML || content?.type === CONTENT_TYPE.MARKDOWN

export const isPublishedApiContent = (content?: PublishedTableOfContents): content is ApiTocItem =>
  content?.type === CONTENT_TYPE.API_URL

export const isPublishedGraphQlContent = (content?: PublishedTableOfContents): content is GraphQlTocItem =>
  content?.type === CONTENT_TYPE.GRAPHQL

export interface TocItemCore {
  id: string,
  title: string,
  content: { type: CONTENT_TYPE }
  children?: TocItemCore[]
}

const findItemRecursively = <T extends TocItemCore = TocItemCore>(tocItem: T, id: string): T | undefined => {
  if (tocItem.id === id) {
    return tocItem
  }
  if (isDocumentContentGeneric(tocItem) && tocItem.children?.length) {
    return tocItem.children
      .map(child => findItemRecursively(child as T, id))
      .filter(Boolean)
      .pop()
  }
  return undefined
}

export const findItem = <T extends TocItemCore>(tocItems: T[], id: string): T | undefined => tocItems
  .map(tocItem => findItemRecursively(tocItem, id))
  .filter(Boolean)
  .pop()

const findPublishedItemRecursively = (tocItem: PublishedTableOfContents, propertyValue: string, property: string = 'path'): PublishedTableOfContents | undefined => {
  if (tocItem[property] === propertyValue) {
    return tocItem
  }
  if (isPublishedDocumentContent(tocItem) && tocItem.children?.length > 0) {
    return tocItem.children
      .map(child => findPublishedItemRecursively(child, propertyValue, property))
      .filter(Boolean)
      .pop()
  }
  return undefined
}

export const findItemByPath = (tocItems: PublishedTableOfContents[], path: string): PublishedTableOfContents | undefined => tocItems
  .map(tocItem => findPublishedItemRecursively(tocItem, path))
  .filter(Boolean)
  .pop()

export const findItemByTocId = (tocItems: PublishedTableOfContents[], contentId: string): PublishedTableOfContents | undefined => tocItems
  .map(tocItem => findPublishedItemRecursively(tocItem, contentId, 'tableOfContentsId'))
  .filter(Boolean)
  .pop()

export const getConsumerContentSlug = ({
  templateId,
  productSlug,
  sectionSlug,
  contentSlug,
}: {
  templateId?: string,
  productSlug?: string,
  sectionSlug?: string,
  contentSlug?: string,
} = {}) => {
  if (!productSlug && !templateId) {
    return '/'
  }
  if (!sectionSlug || !contentSlug) {
    return `/${productSlug || templateId}`
  }
  return `/${productSlug || templateId}/${sectionSlug}/${contentSlug}`
}

export const getProviderContentSlug = ({
  productSlug,
  contentId,
}: {
  productSlug?: string,
  contentId?: string,
} = {}) => {
  if (!productSlug) {
    return '/sp-admin'
  }
  if (!contentId) {
    return `/sp-admin/products/${productSlug}`
  }
  return `/sp-admin/products/${productSlug}/edit/content/${contentId}`
}

export const getProviderTemplateContentSlug = ({
  templateId,
  contentId,
}: {
  templateId?: string,
  contentId?: string,
} = {}) => {
  if (!templateId) {
    return '/sp-admin'
  }
  if (!contentId) {
    return `/sp-admin/templates/${templateId}`
  }
  return `/sp-admin/templates/${templateId}/edit/content/${contentId}`
}

export const convertTOCRecursively = <T, S extends TocItemCore>(items: S[], itemModifier: (item: S) => T): T[] => items.map((item) => {
  if (isDocumentContentGeneric(item)) {
    return ({
      ...itemModifier(item),
      children: convertTOCRecursively(item.children as S[], itemModifier),
    })
  }
  return itemModifier(item)
})

export const haveActiveChild = <T extends TocItemCore>(items: Array<T>, currentItemId: string | undefined, activeItemId: string | undefined): boolean => {
  if (currentItemId === undefined || activeItemId === undefined) {
    return false
  }
  const currentItem = findItem<T>(items, currentItemId)
  const activeItem = currentItem && isDocumentContentGeneric(currentItem) && findItem(currentItem?.children || [], activeItemId)
  return Boolean(activeItem)
}

export const flattenTOC = (items: TableOfContentsItemDiff[]) => items.reduce((flat: TableOfContentsItemDiff[], item: TableOfContentsItemDiff) =>
  'children' in item ? flat.concat(item, flattenTOC(item.children)) : flat.concat(item),
  [] as TableOfContentsItemDiff[])

export const sanitizeId = (id: string) => id.replace(/[^a-zA-Z0-9-_]/g, '_')

export const getConsumerContentPath = (pathname: string, itemPath: string) => `/${pathname.split('/')[1]}/${itemPath}`

export const getTOCBreadcrumbPaths = (tableOfContents: PublishedTableOfContents[], currentPathname: string) => {
  const path: { title: string, path: string, id: string }[] = []
  const productPath = extractProductPathFromUrl(currentPathname)

  const traverse = (items) => {
    for (const item of items) {
      path.push({
        title: item.title,
        path: getConsumerContentPath(currentPathname, item.path),
        id: sanitizeId(`breadcrumb-${item.path}`)
      })
      if (item.path === productPath) {
        return true
      }
      if (item.children && traverse(item.children)) {
        return true
      }
      path.pop()
    }
    return false
  }

  traverse(tableOfContents)

  return path
}

export const extractApiIdentifiers = (items: TableOfContentsItemDiff[]): string[] => {
  const apiIdentifiers: string[] = []

  for (const item of items) {
    if (
      isApiContentDiff(item) &&
      typeof item.content.url === 'string'
    ) {
      const urlParts = item.content.url.split('/apis/')
      if (urlParts.length === 2 && urlParts[1].length > 0) {
        apiIdentifiers.push(urlParts[1])
      }
    }

    if ('children' in item && Array.isArray(item.children) && item.children.length > 0) {
      apiIdentifiers.push(...extractApiIdentifiers(item.children))
    }
  }

  return apiIdentifiers
}